import { ComponentProps, FC } from 'react'

import { useFragment } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { ShiftManagementSwapShiftApprovableFragmentDoc } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import NoteField from 'modules/inbox_tasks/components/NoteField'

import { useEmployeeNameInfo, useShiftDetails } from './helpers'

const ShiftBlock: FC<{
  idx: number
  details: ComponentProps<typeof DetailsItemsList>['details']
  notes?: { label: string; content: string }
}> = ({ idx, details, notes }) => (
  <Box width="full" gap="s16" paddingY="s12">
    <Box width="full" gap="s12">
      <Text size="300" weight="semibold" color="grey1000">
        {i18n.t('shift_management.swap_shifts.shift')} {idx + 1}
      </Text>
      <DetailsItemsList details={details} tableView />
    </Box>
    {!!notes && <NoteField label={notes.label} content={notes.content} />}
  </Box>
)

type ShiftManagementSwapShiftTaskContentProps = {
  id: number
  requesterEmployeeId?: number
}

const ShiftManagementSwapShiftTaskContent: FC<ShiftManagementSwapShiftTaskContentProps> = ({
  id,
  requesterEmployeeId,
}) => {
  const { data } = useFragment({
    fragment: ShiftManagementSwapShiftApprovableFragmentDoc,
    fragmentName: 'ShiftManagementSwapShiftApprovable',
    from: {
      __typename: 'ShiftManagementSwapShift',
      id,
    },
    optimistic: false,
  })

  const currentShift = data?.currentShift
  const requestedShift = data?.requestedShift

  const requesterEmployee =
    requesterEmployeeId === currentShift?.employee?.id
      ? currentShift?.employee?.employeeName
      : requestedShift?.employee?.employeeName

  const otherEmployee =
    requesterEmployeeId === currentShift?.employee?.id
      ? requestedShift?.employee?.employeeName
      : currentShift?.employee?.employeeName

  const requesterNameInfo = useEmployeeNameInfo(requesterEmployee)

  const otherEmployeeNameInfo = useEmployeeNameInfo(otherEmployee)

  const currentShiftDetails = useShiftDetails(currentShift)

  const requestedShiftDetails = useShiftDetails(requestedShift)

  if (
    !data ||
    !currentShift ||
    !requestedShift ||
    !currentShiftDetails ||
    !requestedShiftDetails ||
    !requesterNameInfo ||
    !otherEmployeeNameInfo
  )
    return null

  const { preferredName: requesterPreferredName, fullName: requesterFullName } = requesterNameInfo

  const { preferredName: otherEmployeePreferredName, fullName: otherEmployeeFullName } =
    otherEmployeeNameInfo

  return (
    <Box width="full" gap="s12">
      <Text size="200" color="grey1000">
        <Text size="200" weight="medium" color="grey1000">
          {requesterFullName}
        </Text>
        &nbsp;
        <Text size="200" color="grey1000">
          {i18n.t('shift_management.swap_shifts.swap_wants_to')}
        </Text>
        &nbsp;
        <Text size="200" weight="medium" color="grey1000">
          {otherEmployeeFullName}.
        </Text>
        &nbsp;
        <Text size="200" color="grey1000">
          {i18n.t('shift_management.swap_shifts.swap_preview_changes')}
        </Text>
      </Text>

      <ShiftBlock
        idx={0}
        details={currentShiftDetails}
        notes={
          data.swapReason
            ? {
                label: i18n.t('shift_management.swap_shifts.swap_reason', {
                  employee: requesterPreferredName,
                }),
                content: data.swapReason,
              }
            : undefined
        }
      />

      <ShiftBlock
        idx={1}
        details={requestedShiftDetails}
        notes={
          data.teammateReason
            ? {
                label: i18n.t('shift_management.swap_shifts.teammate_reply', {
                  employee: otherEmployeePreferredName,
                }),
                content: data.teammateReason,
              }
            : undefined
        }
      />

      <Text size="200" color="grey1000">
        {i18n.t('shift_management.swap_shifts.updates_displayed')}
      </Text>
    </Box>
  )
}

export default ShiftManagementSwapShiftTaskContent

export * from './metadata'
